import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getResultsByHardlink } from '@/integrations/supabase/savedResults';
import { useUserData } from '@/context/UserDataContext';
import ResultsFullPage from './ResultsFullPage';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';

const SavedResultsPage = () => {
  const { hardlink } = useParams();
  const navigate = useNavigate();
  const { setUserData } = useUserData();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle('Your Recommendations');

  useEffect(() => {
    const loadSavedResults = async () => {
      if (!hardlink) {
        setError('No results link provided');
        setIsLoading(false);
        return;
      }

      try {
        const fullHardlink = `https://allera.xyz/results/${hardlink}`;
        const result = await getResultsByHardlink(fullHardlink);

        if (!result.success || !result.data) {
          setError(result.error || 'Failed to load saved results');
          setIsLoading(false);
          return;
        }

        // Set the user data in context
        setUserData(prev => ({
          ...prev,
          ...result.data.userData,
          isBasicFormOnly: false
        }));

        // Store recommendations in localStorage for the ResultsFullPage to use
        localStorage.setItem('recommendations', JSON.stringify(result.data.recommendations));

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading saved results:', error);
        setError('An unexpected error occurred while loading your results');
        setIsLoading(false);
      }
    };

    loadSavedResults();
  }, [hardlink, setUserData]);

  if (isLoading) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="mt-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-4 space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-3xl mx-auto">
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
          <div className="mt-4 text-center">
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <ResultsFullPage />;
};

export default SavedResultsPage; 